import { ConfigProvider, Flex, Layout } from "antd";
import { Content } from "antd/es/layout/layout";
import zhCn from "antd/es/locale/zh_CN";
import { useEffect } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import { clearCurrentUserCache, loadCurrentUser } from "./auth/currentUser";
import { buildInfo } from "./buildInfo";
import { config } from "./config";
import { FeedbackProvider } from "./contexts/FeedbackProvider";
import "./index.less";
import PageControl from "./pages/PageControl";
import PageLogin from "./pages/PageLogin";
import PageRegister from "./pages/PageRegister";
import { call, initClient } from "./tsrpc";

const { Header } = Layout;

const MLayout = styled(Layout)`
  height: 100%;
  display: flex;
  min-width: 0;
`;

const MContent = styled(Content)`
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  align-items: stretch;
  overflow: hidden;
`;

const AppHeader = styled(Header)`
  flex: 0 0 64px;
  padding: 0 48px;

  @media (max-width: 768px) {
    flex-basis: 52px;
    height: 52px;
    line-height: 52px;
    padding: 0 14px;
  }
`;

const HeaderFlex = styled(Flex)`
  height: 100%;
  min-width: 0;
  color: #fff;
`;

const Icon = styled.img`
  width: 40px;
  height: 40px;
  flex: 0 0 auto;

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
  }
`;

const AppName = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BuildVersionText = styled.span`
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
`;

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `控制台[${config.app_name}] ${buildInfo.label}`;
    init();
  }, []);

  const init = async () => {
    await initClient();
    const token = localStorage.getItem("token");
    if (token) {
      call("LoginWithToken", { timestamp: Date.now(), token: token })
        .then((res) => {
          if (!res.isSucc) {
            localStorage.removeItem("token");
            clearCurrentUserCache();
            navigate("/login");
          } else {
            void loadCurrentUser(true)
              .then(() => {
                if (location.pathname === "/") navigate("/control");
              })
              .catch(() => {
                localStorage.removeItem("token");
                clearCurrentUserCache();
                navigate("/login");
              });
          }
        })
        .catch((_) => {
          clearCurrentUserCache();
          navigate("/login");
        });
    } else navigate("/login");
  };

  return (
    <ConfigProvider locale={zhCn} modal={{ centered: true }}>
      <FeedbackProvider>
        <MLayout>
          <AppHeader>
            <HeaderFlex align="center">
              <Flex align="center" gap={12} style={{ minWidth: 0 }}>
                <Icon src="/icon.jpg" alt="icon" />
                <AppName>[{config.app_name}] 控制台</AppName>
                <BuildVersionText>{buildInfo.label}</BuildVersionText>
              </Flex>
            </HeaderFlex>
          </AppHeader>
          <MContent>
            <Routes>
              <Route path="/" element={<Navigate to="/login" />} />
              <Route path="/login" element={<PageLogin />} />
              <Route path="/register" element={<PageRegister />} />
              <Route path="/control/*" element={<PageControl />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </MContent>
        </MLayout>
      </FeedbackProvider>
    </ConfigProvider>
  );
};

const container = document.getElementById("app");
const root = ReactDOM.createRoot(container!);
root.render(
  <BrowserRouter
    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
  >
    <App />
  </BrowserRouter>,
);
